/**
 * TopMoversCard — top N tenants/contas com maior variação MoM absoluta (SP3).
 *
 * Deriva os "movers" da lista de `PortfolioTenantRow` recebida por prop:
 *   - Ordena por |mom_pct| decrescente e exibe os primeiros `topN` (default 5).
 *   - Tenants com mom_pct null ficam no final (sem delta).
 *
 * Colunas exibidas: nome, Δ% (formatPct {signed} + cor), Δ US$ (formatMoney {signed}).
 *   - Alta de custo → red.6 | Queda → green.6 | Neutro → dimmed.
 *
 * Não faz fetch interno. Cores apenas via tokens DS/Mantine.
 */
import { Badge, Group, Stack, Table, Text } from '@mantine/core'
import { IconArrowDown, IconArrowUp, IconTrendingUp } from '@tabler/icons-react'

import { WidgetCard } from '@/components/ui'
import { EmptyState } from '@/components/ui'
import { formatMoney } from '@/components/finops/formatMoney'
import { formatPct } from '@/utils/finops'
import type { PortfolioTenantRow } from '@/api/finopsDashboard'

// ─── helpers ────────────────────────────────────────────────────────────────

function momColor(mom: number | null): string {
  if (mom === null) return 'dimmed'
  return mom > 0 ? 'red.6' : 'green.6'
}

function PctArrow({ value }: { value: number | null }) {
  if (value === null || value === 0) return null
  return value > 0
    ? <IconArrowUp size={12} style={{ verticalAlign: 'middle' }} />
    : <IconArrowDown size={12} style={{ verticalAlign: 'middle' }} />
}

// ─── tipos ──────────────────────────────────────────────────────────────────

export interface TopMoversCardProps {
  /** Lista bruta de tenants do portfólio; o card filtra e ordena internamente. */
  rows: PortfolioTenantRow[]
  /** Quantos movers exibir. Default 5. */
  topN?: number
  title?: string
}

// ─── componente ─────────────────────────────────────────────────────────────

export function TopMoversCard({
  rows,
  topN = 5,
  title = 'Top Variações MoM',
}: TopMoversCardProps) {
  // Ordena por |delta_usd| desc; tenants com mom_pct null (novos, delta=positivo mas sem base)
  // ficam no final para não distorcer o ranking de variações.
  const sorted = [...rows].sort((a, b) => {
    // tenant novo (mom_pct null) vai para o final
    if (a.mom_pct === null && b.mom_pct !== null) return 1
    if (b.mom_pct === null && a.mom_pct !== null) return -1
    return Math.abs(b.delta_usd) - Math.abs(a.delta_usd)
  })

  const movers = sorted.slice(0, topN)

  if (movers.length === 0) {
    return (
      <EmptyState
        icon={IconTrendingUp}
        title="Sem dados de variação"
        description="Não há tenants com variação MoM disponível para o período."
        accent="primary"
        size="sm"
      />
    )
  }

  return (
    <WidgetCard accent="none" p={0}>
      {/* cabeçalho */}
      <Group
        px="md"
        py="sm"
        gap="xs"
        style={{ borderBottom: '1px solid var(--ai-border-light)' }}
      >
        <IconTrendingUp size={15} color="var(--ai-brand-azul)" />
        <Text fw={600} size="sm" c="var(--ai-brand-azul)">
          {title}
        </Text>
      </Group>

      {/* tabela compacta */}
      <Table
        striped={false}
        highlightOnHover
        withColumnBorders={false}
        verticalSpacing={6}
        horizontalSpacing="md"
        fz="xs"
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <Text size="xs" c="dimmed" fw={600}>
                Tenant
              </Text>
            </Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>
              <Text size="xs" c="dimmed" fw={600}>
                Δ%
              </Text>
            </Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>
              <Text size="xs" c="dimmed" fw={600}>
                Δ US$
              </Text>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {movers.map((row) => {
            // mom_pct null = tenant novo (sem base histórica para calcular %)
            const isNew = row.mom_pct === null && row.amount_usd > 0
            const color = momColor(row.mom_pct)

            return (
              <Table.Tr key={row.tenant_id}>
                {/* nome */}
                <Table.Td>
                  <Stack gap={1}>
                    <Text size="xs" fw={500} lineClamp={1}>
                      {row.name}
                    </Text>
                    {row.anomaly && (
                      <Badge size="xs" color="yellow" variant="light">
                        anomalia
                      </Badge>
                    )}
                  </Stack>
                </Table.Td>

                {/* Δ% */}
                <Table.Td style={{ textAlign: 'right' }}>
                  {isNew ? (
                    <Badge size="xs" color="teal" variant="light">
                      novo
                    </Badge>
                  ) : (
                    <Group gap={2} justify="flex-end" wrap="nowrap" c={color}>
                      <PctArrow value={row.mom_pct} />
                      <Text size="xs" fw={700} c={color} span>
                        {formatPct(row.mom_pct, { signed: true })}
                      </Text>
                    </Group>
                  )}
                </Table.Td>

                {/* Δ US$ — usa delta_usd real do backend */}
                <Table.Td style={{ textAlign: 'right' }}>
                  <Text size="xs" fw={600} c={color}>
                    {formatMoney(row.delta_usd, { signed: true })}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
    </WidgetCard>
  )
}
